import { useEffect, useState } from "react";
import Shell from "../components/Shell";
import { Link } from "react-router-dom";
import { fetchHolidays, updateHolidayConfig, toggleHoliday, prepareHoliday } from "../lib/api";
import { GiftIcon, WarningIcon, PaperPlaneTiltIcon, CheckCircleIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const RELIGION_STYLE = {
  Christian: "bg-blue-100 text-blue-800",
  Jewish: "bg-indigo-100 text-indigo-800",
  Muslim: "bg-green-100 text-green-800",
  Secular: "bg-zinc-100 text-zinc-700",
};

export default function HolidaysPage() {
  const [config, setConfig] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [targetCount, setTargetCount] = useState(0);
  const [signature, setSignature] = useState("");
  const [sendTime, setSendTime] = useState("09:00");
  const [preparing, setPreparing] = useState("");

  const load = async () => {
    const d = await fetchHolidays();
    setConfig(d.config);
    setHolidays(d.holidays);
    setTargetCount(d.target_group_count);
    setSignature(d.config.signature || "");
    setSendTime(d.config.send_time || "09:00");
  };

  useEffect(() => {
    load();
  }, []);

  const saveConfig = async (patch) => {
    try {
      const cfg = await updateHolidayConfig(patch);
      setConfig(cfg);
      toast.success("Saved");
    } catch {
      toast.error("Could not save");
    }
  };

  const onMasterToggle = () => saveConfig({ enabled: !config.enabled });

  const onHolidayToggle = async (h) => {
    setHolidays((prev) => prev.map((x) => (x.id === h.id ? { ...x, enabled: !x.enabled } : x)));
    try {
      await toggleHoliday(h.id, !h.enabled);
    } catch {
      setHolidays((prev) => prev.map((x) => (x.id === h.id ? h : x)));
      toast.error("Update failed");
    }
  };

  const onPostNow = async (h) => {
    if (targetCount === 0) return toast.error("Enable Holiday greetings for at least one group first (Groups page)");
    if (!window.confirm(`Generate an AI image + greeting for ${h.name} and schedule it to ${targetCount} group(s) in ~2 minutes?`)) return;
    setPreparing(h.id);
    try {
      await prepareHoliday(h.id);
      toast.success(`${h.name} greeting scheduled — review it on the Broadcast page`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule");
    } finally {
      setPreparing("");
    }
  };

  if (!config) return <Shell title="Holiday greetings"><div className="p-10 text-sm text-zinc-400">Loading…</div></Shell>;

  return (
    <Shell title="Holiday greetings" subtitle="Auto-post warm wishes for the major holidays to your opted-in groups.">
      <div className="max-w-4xl space-y-6" data-testid="holidays-page">
        {/* Master control */}
        <div className="border border-zinc-200 bg-white p-4 space-y-4">
          <label className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-zinc-900">Automatic holiday greetings</div>
              <div className="text-xs text-zinc-500">When on, greetings auto-generate the day before and send at your chosen time. You're notified to review/cancel.</div>
            </div>
            <button
              data-testid="holiday-master-toggle"
              onClick={onMasterToggle}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center transition-colors ${config.enabled ? "bg-zinc-950" : "bg-zinc-300"}`}
            >
              <span className={`inline-block h-4 w-4 bg-white transition-transform ${config.enabled ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Send time (Slovenia)</label>
              <input
                data-testid="holiday-send-time"
                type="time"
                value={sendTime}
                onChange={(e) => setSendTime(e.target.value)}
                onBlur={() => sendTime !== config.send_time && saveConfig({ send_time: sendTime })}
                className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:border-zinc-900"
              />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-zinc-500">Signature</label>
              <input
                data-testid="holiday-signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                onBlur={() => signature !== config.signature && saveConfig({ signature })}
                className="mt-1 w-full border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:border-zinc-900"
              />
            </div>
          </div>
        </div>

        {/* Group opt-in status */}
        <div className={`flex items-start gap-3 border p-3 text-sm ${targetCount === 0 ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`} data-testid="holiday-group-status">
          {targetCount === 0 ? <WarningIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-amber-600" /> : <CheckCircleIcon size={20} weight="fill" className="mt-0.5 shrink-0 text-emerald-600" />}
          <p>
            {targetCount === 0 ? (
              <>No groups are opted in yet. Turn on the <GiftIcon size={14} className="inline" /> <strong>Holiday greetings</strong> toggle for the groups you want on the <Link to="/groups" className="underline">Groups page</Link>.</>
            ) : (
              <><strong>{targetCount}</strong> group(s) will receive holiday greetings. Manage them via the <GiftIcon size={14} className="inline" /> toggle on the <Link to="/groups" className="underline">Groups page</Link>.</>
            )}
          </p>
        </div>

        {/* Holiday list */}
        <div className="border border-zinc-200 bg-white" data-testid="holiday-list">
          {holidays.length === 0 && <p className="p-6 text-sm text-zinc-400">No upcoming holidays configured.</p>}
          {holidays.map((h) => (
            <div key={h.id} data-testid={`holiday-${h.id}`} className="flex items-center gap-3 border-b border-zinc-100 px-4 py-3 last:border-b-0">
              <button
                data-testid={`holiday-toggle-${h.id}`}
                onClick={() => onHolidayToggle(h)}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center transition-colors ${h.enabled ? "bg-zinc-950" : "bg-zinc-300"}`}
                title={h.enabled ? "Enabled" : "Disabled"}
              >
                <span className={`inline-block h-3.5 w-3.5 bg-white transition-transform ${h.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-zinc-900">{h.name}</span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-medium uppercase ${RELIGION_STYLE[h.religion] || RELIGION_STYLE.Secular}`}>{h.religion}</span>
                  {h.status === "scheduled" && <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase bg-green-100 text-green-800">scheduled</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-zinc-500">
                  <span>{new Date(h.date + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>
                  <span>·</span>
                  <span>{h.days_until < 0 ? "past" : h.days_until === 0 ? "today" : `in ${h.days_until} day${h.days_until === 1 ? "" : "s"}`}</span>
                  <span>·</span>
                  <span className="italic truncate">{h.greeting}</span>
                </div>
              </div>
              <button
                data-testid={`holiday-postnow-${h.id}`}
                onClick={() => onPostNow(h)}
                disabled={preparing === h.id || targetCount === 0}
                title="Generate + schedule this greeting now (fires in ~2 min)"
                className="flex shrink-0 items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs text-zinc-800 hover:bg-zinc-50 disabled:opacity-40"
              >
                <PaperPlaneTiltIcon size={14} />
                {preparing === h.id ? "Scheduling…" : "Post now"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
